import styled from '@emotion/styled';

import _MerpayIcon from '@/assets/icons/icon_merpay.svg';
import _PaypayIcon from '@/assets/icons/icon_paypay.svg';
import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { hoverOpacity } from '@/utility/theme/animation';
import { colorPaletteTheme } from '@/utility/theme/color';

const bgImg = (path: string) => {
  return {
    backgroundImage: `url(${path})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  };
};

export const ContentWrapper = styled.div(() =>
  mq({
    overflowY: 'auto',
  }),
);

export const TopBox = styled.div(() =>
  mq({
    borderBottom: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
    paddingBottom: defaultTheme.spacing.xxl,
  }),
);

export const ExternalLinkWrapper = styled.div(() =>
  mq({
    display: 'grid',
    gridTemplateColumns: [
      'repeat(2, minmax(120px, 178px))',
      'repeat(2, minmax(120px, 178px))',
      'repeat(3, 178px)',
      'repeat(4, 178px)',
    ],
    gridGap: defaultTheme.spacing.s,
    justifyContent: 'center',
    marginTop: defaultTheme.spacing.xxl,
    '& button': {
      borderWidth: '1px',
    },
  }),
);

export const AppealWrapper = styled.div(() =>
  mq({
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    '& > span': {
      color: defaultTheme.text.error,
      fontSize: [
        defaultTheme.font.size.xs,
        defaultTheme.font.size.xs,
        defaultTheme.font.size.s,
        defaultTheme.font.size.s,
      ],
      fontWeight: 'bold',
    },
  }),
);

export const Title = styled.p(() =>
  mq({
    textAlign: 'center',
    fontSize: defaultTheme.font.size.l,
    fontWeight: 'bold',
    margin: `${defaultTheme.spacing.s} 0 ${defaultTheme.spacing.xxl}`,
  }),
);

export const Note = styled.p(() =>
  mq({
    display: 'flex',
    justifyContent: 'center',
    color: defaultTheme.text.gray,
    fontSize: defaultTheme.font.size.xs,
    margin: `${defaultTheme.spacing.xxl} 0 ${defaultTheme.spacing.s}`,
    '&::before': {
      content: '"※"',
      marginRight: defaultTheme.spacing.xs,
    },
  }),
);

export const RadioWrapper = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: ['column', 'column', 'row', 'row'],
    justifyContent: 'center',
    fontWeight: 'bold',
    marginBottom: defaultTheme.spacing.xl,
    '& > div:first-of-type': {
      marginRight: [0, 0, '40px', '40px'],
      marginBottom: [defaultTheme.spacing.m, defaultTheme.spacing.m, 0, 0],
    },
    '& span': {
      fontSize: defaultTheme.font.size.xs,
      fontWeight: 'normal',
    },
  }),
);

export const InputWrapper = styled.div(() =>
  mq({
    display: 'grid',
    gridTemplateColumns: '1fr 32px',
    gridGap: defaultTheme.spacing.s,
    alignItems: 'end',
    maxWidth: ['auto', 'auto', '360px', '360px'],
    margin: '0 auto',
    '& > span': {
      display: 'block',
      width: '32px',
      fontSize: defaultTheme.font.size.m,
      fontWeight: 'bold',
    },
  }),
);

export const AmountWrapper = styled.div(() =>
  mq({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(80px, 184px))',
    gridGap: defaultTheme.spacing.s,
    justifyContent: 'center',
    margin: `${defaultTheme.spacing.l} 0 ${defaultTheme.spacing.xxl}`,
    '& > button': {
      borderWidth: '1px',
    },
  }),
);

export const SubmitWrapper = styled.div(() =>
  mq({
    maxWidth: '320px',
    margin: `${defaultTheme.spacing.xxl} auto 0`,
  }),
);

export const LinkWrapper = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    '& > span': {
      color: defaultTheme.text.gray,
      fontSize: defaultTheme.font.size.xs,
      marginTop: defaultTheme.spacing.l,
    },
  }),
);

export const BannerWrapper = styled.div(() =>
  mq({
    display: 'flex',
    flexWrap: 'wrap',
    gridGap: defaultTheme.spacing.l,
    justifyContent: 'center',
    width: 'fit-content',
    margin: `${defaultTheme.spacing.xxl} auto`,
    '& > a': {
      width: 'auto',
    },
  }),
);

export const ErrorWrapper = styled.div(() =>
  mq({
    color: defaultTheme.color.error,
    textAlign: 'left',
  }),
);

export const Error = styled.p(() =>
  mq({
    color: defaultTheme.text.error,
    fontSize: defaultTheme.font.size.xs,
    lineHeight: defaultTheme.font.height.base,
    '&::before': {
      content: '"※"',
    },
  }),
);

export const RegisterWrapper = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: defaultTheme.font.size.xs,
    marginTop: defaultTheme.spacing.l,
    '& > p': {
      margin: `${defaultTheme.spacing.s} 0`,
    },
    a: {
      textDecorationLine: 'underline',
      ...hoverOpacity,
    },
  }),
);

export const PaypayIcon = styled.i(() =>
  mq({
    display: 'block',
    width: '32px',
    height: '32px',
    marginRight: defaultTheme.spacing.s,
    ...bgImg(_PaypayIcon),
  }),
);

export const MerpayIcon = styled.i(() =>
  mq({
    display: 'block',
    width: '20px',
    height: '30px',
    marginRight: defaultTheme.spacing.s,
    ...bgImg(_MerpayIcon),
  }),
);

export const Acknowledgment = styled.p(() =>
  mq({
    color: defaultTheme.text.gray,
    fontSize: defaultTheme.font.size.xs,
    paddingTop: defaultTheme.spacing.xxl,
  }),
);

export const HowtoLinkWrapper = styled.div(() =>
  mq({
    textAlign: 'center',
    margin: `${defaultTheme.spacing.xxl} 0`,
    '& > a': {
      color: defaultTheme.text.gray,
      fontSize: defaultTheme.font.size.xs,
      textDecorationLine: 'underline',
      ...hoverOpacity,
    },
  }),
);

export const BannerBorder = styled.div(() =>
  mq({
    border: '1px solid',
    borderColor: colorPaletteTheme.grayScale.mediumLight,
    borderRadius: defaultTheme.border.radius.m,
  }),
);

export const CreditDormantWrapper = styled.div(() =>
  mq({
    margin: `${defaultTheme.spacing.xxl} auto 0`,
    textAlign: 'center',
  }),
);

export const CreditDormantTitle = styled.p(() =>
  mq({
    fontWeight: 'bold',
    color: defaultTheme.text.error,
    fontSize: defaultTheme.font.size.m,
  }),
);

export const CreditDormantNote = styled.p(() =>
  mq({
    marginTop: defaultTheme.spacing.m,
    color: defaultTheme.text.gray,
    fontSize: defaultTheme.font.size.s,
  }),
);

export const CreditDormantLink = styled.div(() =>
  mq({
    marginTop: defaultTheme.spacing.m,
    '& > a': {
      color: defaultTheme.text.gray,
      fontSize: defaultTheme.font.size.s,
      textDecorationLine: 'underline',
      ...hoverOpacity,
    },
  }),
);
